import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { ProposalContent, ProposalImages, AdditionalPhoto, SavedProposal, TimelineItem } from "@/types/proposal";
import { ProposalPage1 } from "@/components/proposal/ProposalPage1";
import { ProposalPage2 } from "@/components/proposal/ProposalPage2";
import { ProposalPage3 } from "@/components/proposal/ProposalPage3";
import { BuilderPanel } from "@/components/builder/BuilderPanel";
import { SavedProposalsDialog } from "@/components/builder/SavedProposalsDialog";
import { SaveProposalDialog } from "@/components/builder/SaveProposalDialog";
import { toast } from "sonner";

const Index = () => {
  const navigate = useNavigate();
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const urlParams = new URLSearchParams(search);
  const isClientMode = urlParams.get('client') === 'true';
  const creatorUserId = urlParams.get('creator') || undefined;
  const proposalNameParam = urlParams.get('proposal') || undefined;

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedProposals, setSavedProposals] = useState<SavedProposal[]>([]);
  const [currentProposalName, setCurrentProposalName] = useState('');
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Check authentication - only for builder mode
  useEffect(() => {
    if (isClientMode) {
      // Validate creator exists when in client mode
      if (creatorUserId) {
        validateCreatorAccess(creatorUserId);
      } else {
        setLoading(false);
      }
      return;
    }

    if (typeof window === 'undefined') return; // SSR-safe

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (!session) {
        navigate("/auth");
      } else {
        // Load company settings from database
        setTimeout(() => {
          loadCompanySettings(session.user.id);
        }, 0);
      }
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (!session) {
        navigate("/auth");
      } else {
        loadCompanySettings(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isClientMode]);

  // Validate creator access for client mode
  const validateCreatorAccess = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("company_settings")
        .select("user_id")
        .eq("user_id", userId)
        .maybeSingle();

      if (error || !data) {
        console.error("Invalid creator access attempt:", userId);
        toast.error("Invalid proposal link. Please contact the proposal creator.");
        setLoading(false);
        return false;
      }
      setLoading(false);
      return true;
    } catch (error) {
      console.error("Creator validation error:", error);
      toast.error("Unable to validate access. Please try again.");
      setLoading(false);
      return false;
    }
  };

  const loadCompanySettings = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("company_settings")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setContent(prev => ({
          ...prev,
          companyAddress: data.company_address,
          companyPhone: data.company_phone,
          companyEmail: data.company_email,
          companyWebsite: data.company_website,
          tagline: data.tagline,
        }));
        
        // Load logo from settings
        if (data.company_logo) {
          setImages(prev => ({
            ...prev,
            logo: data.company_logo,
          }));
        }
      }
    } catch (error: any) {
      console.error("Error loading company settings:", error);
    }
  };

  const [images, setImages] = useState<ProposalImages>({
    logo: null,
    floorPlan: null,
    optionImages: {},
    vanityImages: {},
  });

  const [additionalPhotos, setAdditionalPhotos] = useState<AdditionalPhoto[]>([]);

  const [content, setContent] = useState<ProposalContent>({
    title: "Custom Cabinet Proposal",
    projectName: "",
    projectNumber: "",
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    clientName: "",
    executiveSummary: "We are pleased to present this proposal for custom cabinetry. Our proposal includes multiple style options, allowing you to choose the perfect aesthetic for your space.",
    totalInvestment: "",
    warrantyYears: "3",
    warrantyText: "This comprehensive package includes professional build, delivery, and installation services, all backed by our workmanship warranty for your complete peace of mind.",
    numberOfOptions: 1,
    cabinetOptions: [
      {
        id: "option-1",
        title: "Option 1",
        description: "Description for this cabinet style...",
        vanityTitle: "Option 1 Vanity",
        vanityDescription: "Description for this vanity style...",
      },
    ],
    floorPlanDescription: "Plan view showing placement of sink, dishwasher, refrigerator, range, and cabinetry.",
    onlinePlansLink: "",
    sketchupLink: "",
    closingNote: "Thank you for considering our services. We look forward to creating cabinetry that's the perfect fit for your home.",
    tagline: "Driven by Precision. Evolved by Design.",
    timeline: [
      { period: "WEEKS 1—2", task: "Design Development & Final Approval" },
      { period: "WEEKS 3—6", task: "Material Procurement & Preparation" },
      { period: "WEEKS 7—10", task: "Custom Fabrication & Finishing" },
      { period: "WEEKS 11—12", task: "Professional Installation" },
    ],
    nextSteps: [
      "Review and approve proposal",
      "Sign agreement",
      "Submit deposit to secure schedule",
    ],
    companyAddress: "866 N Columbia Blvd suite b 111, Portland, OR 97217",
    companyPhone: "(971) 272-0955",
    companyEmail: "info@foundrypdx.com",
    companyWebsite: "www.foundrypdx.com",
  });

  // Load saved proposals from database (or localStorage fallback)
  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR-safe
    if (!isClientMode && session) {
      loadSavedProposalsList();
    } else if (typeof window !== 'undefined') {
      // Fallback to localStorage for client mode or no session
      const saved = localStorage.getItem('foundryProposals');
      if (saved) {
        try {
          setSavedProposals(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to load saved proposals:', e);
          localStorage.removeItem('foundryProposals');
        }
      }
    }
  }, [session, isClientMode]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (currentProposalName) {
      const autoSaveInterval = setInterval(() => {
        saveProposal(currentProposalName, false);
      }, 30000);
      return () => clearInterval(autoSaveInterval);
    }
  }, [content, images, additionalPhotos, currentProposalName]);

  const handleImageUpload = (imageType: keyof ProposalImages, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setImages((prev) => ({ ...prev, [imageType]: dataUrl }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOptionImageUpload = (optionId: string, type: 'main' | 'vanity', file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (type === 'main') {
        setImages((prev) => ({
          ...prev,
          optionImages: { ...prev.optionImages, [optionId]: dataUrl }
        }));
      } else {
        setImages((prev) => ({
          ...prev,
          vanityImages: { ...prev.vanityImages, [optionId]: dataUrl }
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleOptionImageDrop = (optionId: string, type: 'main' | 'vanity', dataUrl: string) => {
    if (type === 'main') {
      setImages((prev) => ({
        ...prev,
        optionImages: { ...prev.optionImages, [optionId]: dataUrl }
      }));
    } else {
      setImages((prev) => ({
        ...prev,
        vanityImages: { ...prev.vanityImages, [optionId]: dataUrl }
      }));
    }
    toast.success('Image updated!');
  };

  const handleLogoDrop = async (dataUrl: string) => {
    setImages((prev) => ({
      ...prev,
      logo: dataUrl
    }));
    try {
      if (session?.user?.id) {
        const { error } = await supabase
          .from('company_settings')
          .update({ company_logo: dataUrl })
          .eq('user_id', session.user.id);
        if (error) {
          await supabase.from('company_settings').insert([{ user_id: session.user.id, company_logo: dataUrl }]);
        }
      }
      toast.success('Logo saved to company settings!');
    } catch (e) {
      console.error('Failed to persist logo:', e);
      toast.error('Logo updated for this proposal. Use Admin Settings to save company logo.');
    }
  };

  const handleFloorPlanDrop = (dataUrl: string) => {
    setImages((prev) => ({
      ...prev,
      floorPlan: dataUrl
    }));
    toast.success('Floor plan updated!');
  };

  const handleAdditionalPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    files.forEach((file) => {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newPhoto: AdditionalPhoto = {
            id: Date.now() + Math.random(),
            dataUrl: e.target?.result as string,
            caption: file.name.replace(/\.[^/.]+$/, ''),
            isInspiration: false,
            showInClient: true,
          };
          setAdditionalPhotos((prev) => [...prev, newPhoto]);
        };
        reader.readAsDataURL(file);
      }
    });
    event.target.value = '';
  };

  const handleInspirationPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    files.forEach((file) => {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newPhoto: AdditionalPhoto = {
            id: Date.now() + Math.random(),
            dataUrl: e.target?.result as string,
            caption: file.name.replace(/\.[^/.]+$/, ''),
            isInspiration: true,
            showInClient: false,
          };
          setAdditionalPhotos((prev) => [...prev, newPhoto]);
        };
        reader.readAsDataURL(file);
      }
    });
    event.target.value = '';
  };

  const togglePhotoVisibility = (id: number) => {
    setAdditionalPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, showInClient: !p.showInClient } : p))
    );
  };

  const removeAdditionalPhoto = (id: number) => {
    if (confirm('Remove this photo?')) {
      setAdditionalPhotos((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const updatePhotoCaption = (id: number, caption: string) => {
    setAdditionalPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, caption } : p))
    );
  };

  const handleContentChange = (field: keyof ProposalContent, value: any) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const handleTimelineChange = (index: number, field: keyof TimelineItem, value: string) => {
    setContent((prev) => ({
      ...prev,
      timeline: prev.timeline.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleNextStepChange = (index: number, value: string) => {
    setContent((prev) => ({
      ...prev,
      nextSteps: prev.nextSteps.map((s, i) => (i === index ? value : s)),
    }));
  };

  const addTimelineItem = () => {
    setContent((prev) => ({
      ...prev,
      timeline: [...prev.timeline, { period: 'NEW PERIOD', task: 'New Task' }],
    }));
  };

  const removeTimelineItem = (index: number) => {
    setContent((prev) => ({
      ...prev,
      timeline: prev.timeline.filter((_, i) => i !== index),
    }));
  };

  const addNextStep = () => {
    setContent((prev) => ({
      ...prev,
      nextSteps: [...prev.nextSteps, 'New step'],
    }));
  };

  const removeNextStep = (index: number) => {
    setContent((prev) => ({
      ...prev,
      nextSteps: prev.nextSteps.filter((_, i) => i !== index),
    }));
  };

  // Helper: Upload base64 image to Supabase Storage
  const uploadImageToStorage = async (base64Data: string | null, path: string): Promise<string | null> => {
    if (!base64Data || !session) return null;
    
    try {
      // Convert base64 to blob
      const base64Content = base64Data.split(',')[1];
      const mimeType = base64Data.match(/data:([^;]+);/)?.[1] || 'image/png';
      const blob = await fetch(`data:${mimeType};base64,${base64Content}`).then(r => r.blob());
      
      // Upload to storage
      const filePath = `${session.user.id}/${path}`;
      const { data, error } = await supabase.storage
        .from('proposal-media')
        .upload(filePath, blob, { upsert: true, contentType: mimeType });
      
      if (error) throw error;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('proposal-media')
        .getPublicUrl(filePath);
      
      return publicUrl;
    } catch (error) {
      console.error('Failed to upload image:', error);
      return base64Data; // Fallback to base64 if upload fails
    }
  };

  const saveProposal = async (name: string, showAlert = true) => {
    if (!session) {
      toast.error('You must be logged in to save proposals.');
      return;
    }
    
    try {
      if (showAlert) {
        toast.loading('Saving proposal...', { id: 'save-proposal' });
      }
      
      // Ensure logo is from company settings if not already set
      let finalImages = { ...images };
      if (!finalImages.logo && session.user.id) {
        const { data: companyData } = await supabase
          .from('company_settings')
          .select('company_logo')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (companyData?.company_logo) {
          finalImages.logo = companyData.company_logo;
        }
      }
      
      // Upload all images to storage
      const uploadedImages: ProposalImages = {
        logo: await uploadImageToStorage(finalImages.logo, `${name}/logo.png`),
        floorPlan: await uploadImageToStorage(finalImages.floorPlan, `${name}/floorplan.png`),
        optionImages: {},
        vanityImages: {},
      };
      
      // Upload option images
      for (const [key, value] of Object.entries(finalImages.optionImages)) {
        uploadedImages.optionImages[key] = await uploadImageToStorage(value, `${name}/option_${key}.png`);
      }
      
      // Upload vanity images
      for (const [key, value] of Object.entries(finalImages.vanityImages)) {
        uploadedImages.vanityImages[key] = await uploadImageToStorage(value, `${name}/vanity_${key}.png`);
      }
      
      // Upload additional photos
      const uploadedPhotos = await Promise.all(
        additionalPhotos.map(async (photo, idx) => ({
          ...photo,
          dataUrl: await uploadImageToStorage(photo.dataUrl, `${name}/photo_${idx}.png`) || photo.dataUrl,
        }))
      );
      
      // Save to database
      const { error } = await supabase
        .from('saved_proposals')
        .upsert({
          user_id: session.user.id,
          proposal_name: name,
          content: content as any,
          images: uploadedImages as any,
          additional_photos: uploadedPhotos as any,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,proposal_name'
        });
      
      if (error) throw error;
      
      setCurrentProposalName(name);
      
      // Refresh saved proposals list
      await loadSavedProposalsList();
      
      if (showAlert) {
        toast.success(`Proposal "${name}" saved successfully!`, { id: 'save-proposal' });
      }
    } catch (e: any) {
      console.error('Failed to save proposal:', e);
      if (showAlert) {
        toast.error('Failed to save proposal. Please try again.', { id: 'save-proposal' });
      }
    }
  };

  const loadProposal = async (name: string) => {
    if (!session) {
      toast.error('You must be logged in to load proposals.');
      return;
    }
    
    try {
      toast.loading('Loading proposal...', { id: 'load-proposal' });
      
      // Try loading from database first
      const { data, error } = await supabase
        .from('saved_proposals')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('proposal_name', name)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        setContent(data.content as any as ProposalContent);
        const loadedImages = data.images as any as ProposalImages;
        
        // Ensure company logo is included from company_settings if not in saved proposal
        if (!loadedImages.logo && session?.user?.id) {
          const { data: companyData } = await supabase
            .from('company_settings')
            .select('company_logo')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (companyData?.company_logo) {
            loadedImages.logo = companyData.company_logo;
          }
        }
        
        setImages(loadedImages);
        
        // Handle additional photos with backward compatibility
        const photos = ((data.additional_photos as any) || []).map((photo: AdditionalPhoto) => ({
          ...photo,
          isInspiration: photo.isInspiration ?? false,
          showInClient: photo.showInClient ?? true,
        }));
        setAdditionalPhotos(photos);
        
        setCurrentProposalName(name);
        toast.success(`Proposal "${name}" loaded successfully!`, { id: 'load-proposal' });
      } else {
        // Fallback to localStorage for backward compatibility
        const proposals = JSON.parse(localStorage.getItem('foundryProposals') || '[]');
        const proposal = proposals.find((p: SavedProposal) => p.name === name);
        if (proposal) {
          setContent(proposal.content);
          setImages(proposal.images);
          
          const photos = (proposal.additionalPhotos || []).map((photo: AdditionalPhoto) => ({
            ...photo,
            isInspiration: photo.isInspiration ?? false,
            showInClient: photo.showInClient ?? true,
          }));
          setAdditionalPhotos(photos);
          
          setCurrentProposalName(name);
          toast.success(`Proposal "${name}" loaded from local storage!`, { id: 'load-proposal' });
        } else {
          toast.error(`Proposal "${name}" not found.`, { id: 'load-proposal' });
        }
      }
    } catch (e) {
      console.error('Failed to load proposal:', e);
      toast.error('Failed to load proposal. Please try again.', { id: 'load-proposal' });
    }
  };

  const deleteProposal = async (name: string) => {
    if (!session) {
      toast.error('You must be logged in to delete proposals.');
      return;
    }
    
    if (confirm(`Are you sure you want to delete proposal "${name}"?`)) {
      try {
        toast.loading('Deleting proposal...', { id: 'delete-proposal' });
        
        // Delete images from storage
        const { data: files } = await supabase.storage
          .from('proposal-media')
          .list(`${session.user.id}/${name}`);
        
        if (files && files.length > 0) {
          const filePaths = files.map(f => `${session.user.id}/${name}/${f.name}`);
          await supabase.storage
            .from('proposal-media')
            .remove(filePaths);
        }
        
        // Delete from database
        const { error } = await supabase
          .from('saved_proposals')
          .delete()
          .eq('user_id', session.user.id)
          .eq('proposal_name', name);
        
        if (error) throw error;
        
        if (currentProposalName === name) setCurrentProposalName('');
        
        // Refresh proposals list
        await loadSavedProposalsList();
        
        toast.success(`Proposal "${name}" deleted.`, { id: 'delete-proposal' });
      } catch (e) {
        console.error('Failed to delete proposal:', e);
        toast.error('Failed to delete proposal. Please try again.', { id: 'delete-proposal' });
      }
    }
  };

  // Helper: Load saved proposals list from database
  const loadSavedProposalsList = async () => {
    if (!session) return;
    
    try {
      const { data, error } = await supabase
        .from('saved_proposals')
        .select('proposal_name, created_at, updated_at')
        .eq('user_id', session.user.id)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      
      // Convert to SavedProposal format for UI compatibility
      const proposals: SavedProposal[] = (data || []).map(p => ({
        name: p.proposal_name,
        content: {} as ProposalContent,
        images: {} as ProposalImages,
        additionalPhotos: [],
        savedAt: p.updated_at,
      }));
      
      setSavedProposals(proposals);
    } catch (e) {
      console.error('Failed to load proposals list:', e);
      // Fallback to localStorage
      const proposals = JSON.parse(localStorage.getItem('foundryProposals') || '[]');
      setSavedProposals(proposals);
    }
  };

  const handleSaveClick = () => {
    setShowSaveDialog(true);
  };

  const handleViewSignedProposal = (proposal: any) => {
    try {
      // Load the signed proposal content into the builder
      setContent(proposal.proposal_content);
      // Note: Images are embedded in the proposal_content as data URLs
      setImages({
        logo: null,
        floorPlan: null,
        optionImages: {},
        vanityImages: {}
      });
      setAdditionalPhotos([]);
      setCurrentProposalName(`${proposal.proposal_name} (Signed by ${proposal.client_name})`);
      toast.success('Signed proposal loaded for viewing');
    } catch (error) {
      console.error('Failed to load signed proposal:', error);
      toast.error('Failed to load signed proposal');
    }
  };

  const handleExportSignedPDF = async (proposal: any) => {
    try {
      toast.info('Generating signed PDF...');

      // Get the current proposal container
      const container = document.querySelector('.proposal-container') as HTMLElement | null;
      if (!container) {
        toast.error('Nothing to export: proposal preview not found');
        return;
      }

      // Clone to avoid mutating the live preview
      const clone = container.cloneNode(true) as HTMLElement;

      // Remove print:hidden elements (like photo galleries), builder controls, etc.
      clone.querySelectorAll('.print\\:hidden, [data-lovable], .cursor-move, [class*="border-dashed"], input[type="file"]').forEach(el => el.remove());

      // Add signature section to the cloned content
      const signatureSection = document.createElement('div');
      signatureSection.className = 'p-12 border-t-2 border-slate-300 bg-slate-50';
      signatureSection.innerHTML = `
        <div class="max-w-4xl mx-auto">
          <div class="text-center mb-6">
            <div class="inline-block bg-green-100 text-green-800 px-6 py-2 rounded-full font-semibold text-lg">
              ✓ SIGNED
            </div>
          </div>
          <div class="grid grid-cols-2 gap-8 mb-6">
            <div>
              <p class="text-sm text-slate-600 mb-1">Client Name</p>
              <p class="font-semibold text-slate-800">${proposal.client_name}</p>
            </div>
            ${proposal.client_title ? `
            <div>
              <p class="text-sm text-slate-600 mb-1">Title</p>
              <p class="font-semibold text-slate-800">${proposal.client_title}</p>
            </div>` : ''}
            <div>
              <p class="text-sm text-slate-600 mb-1">Date Signed</p>
              <p class="font-semibold text-slate-800">${new Date(proposal.signed_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div>
            <p class="text-sm text-slate-600 mb-2">Signature</p>
            <div class="bg-white border-2 border-slate-300 rounded p-4">
              <img src="${proposal.signature_data}" alt="Client Signature" class="max-h-24" />
            </div>
          </div>
        </div>
      `;
      clone.appendChild(signatureSection);

      // Upload base64 images to storage and replace with HTTPS URLs
      const uploadCache = new Map<string, string>();
      
      const uploadImageAndGetUrl = async (dataUrl: string): Promise<string | null> => {
        if (uploadCache.has(dataUrl)) {
          console.log('Using cached image URL');
          return uploadCache.get(dataUrl)!;
        }
        
        try {
          console.log(`Processing image for upload (size: ~${Math.round(dataUrl.length * 0.75 / 1024)}KB)...`);
          const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
          if (!matches) {
            console.error('Invalid data URL format');
            return null;
          }
          
          const [, mimeType, base64Data] = matches;
          const fileExt = mimeType.split('/')[1] || 'png';
          console.log(`Image type: ${mimeType}`);
          
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
          
          const fileName = `pdf-export/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          console.log(`Uploading to storage: ${fileName}`);
          
          const { error: uploadError } = await supabase.storage
            .from('proposal-media')
            .upload(fileName, blob, {
              contentType: mimeType,
              cacheControl: '3600',
            });
          
          if (uploadError) {
            console.error('Storage upload error:', uploadError);
            throw uploadError;
          }
          
          const { data: { publicUrl } } = supabase.storage
            .from('proposal-media')
            .getPublicUrl(fileName);
          
          console.log(`Successfully uploaded to: ${publicUrl}`);
          uploadCache.set(dataUrl, publicUrl);
          return publicUrl;
        } catch (e) {
          console.error('Failed to upload image:', e);
          return null;
        }
      };
      
      // Process all <img> tags - upload base64 data URLs to storage
      const images = clone.querySelectorAll('img');
      console.log(`Found ${images.length} images to process for signed PDF`);
      await Promise.all(
        Array.from(images).map(async (img, idx) => {
          const originalSrc = img.src || '';
          console.log(`Image ${idx} (${img.alt || 'no alt'}): src=${originalSrc.substring(0, 100)}...`);
          
          if (!originalSrc) {
            console.warn(`Image ${idx} has no src`);
            return;
          }
          
          // Handle data URLs - upload directly, add eager loading for logos
          if (originalSrc.startsWith('data:')) {
            console.log(`Processing data URL image ${idx}...`);
            const publicUrl = await uploadImageAndGetUrl(originalSrc);
            if (publicUrl) {
              console.log(`Successfully uploaded data URL image ${idx} to ${publicUrl}`);
              img.src = publicUrl;
              img.setAttribute('loading', 'eager');
              img.setAttribute('decoding', 'sync');
            } else {
              console.warn(`Failed to upload data URL image ${idx}, keeping data URL as fallback`);
            }
          } else if (originalSrc.startsWith(window.location.origin) || originalSrc.startsWith('/') || originalSrc.startsWith('./')) {
            // Convert local asset URL to public storage URL so external PDF service can access it
            try {
              console.log(`Externalizing local asset image ${idx}...`);
              const fullUrl = originalSrc.startsWith('/') || originalSrc.startsWith('./') 
                ? `${window.location.origin}${originalSrc.startsWith('./') ? originalSrc.substring(1) : originalSrc}` 
                : originalSrc;
              const res = await fetch(fullUrl);
              const blob = await res.blob();
              const mimeType = blob.type || 'image/png';
              const ext = (mimeType.split('/')[1] || 'png').split(';')[0];
              const fileName = `pdf-export/${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
              const { error: uploadError } = await supabase.storage
                .from('proposal-media')
                .upload(fileName, blob, { contentType: mimeType, cacheControl: '3600' });
              if (uploadError) throw uploadError;
              const { data: { publicUrl } } = supabase.storage
                .from('proposal-media')
                .getPublicUrl(fileName);
              console.log(`Local asset image ${idx} uploaded to ${publicUrl}`);
              img.src = publicUrl;
              img.setAttribute('loading', 'eager');
              img.setAttribute('decoding', 'sync');
            } catch (e) {
              console.error(`Failed to externalize local asset image ${idx}:`, e);
              // Keep original src as fallback
            }
          } else {
            console.log(`Image ${idx} already has public URL, keeping as-is`);
          }
        })
      );
      
      // Process background images using getComputedStyle to catch Tailwind bg-[url(...)]
      const bgElements = Array.from(clone.querySelectorAll('*')) as HTMLElement[];
      await Promise.all(
        bgElements.map(async (el) => {
          const computedBg = window.getComputedStyle(el).backgroundImage;
          if (!computedBg || computedBg === 'none') return;
          
          // Handle data URL images
          const match = computedBg.match(/url\(['"]?(data:[^'"]+)['"]?\)/);
          if (match) {
            const dataUrl = match[1];
            const publicUrl = await uploadImageAndGetUrl(dataUrl);
            if (publicUrl) {
              el.style.backgroundImage = `url(${publicUrl})`;
            }
          }
          // Preserve gradient backgrounds
          else if (computedBg.startsWith('linear-gradient')) {
            el.style.backgroundImage = computedBg;
          }
        })
      );

      // Add inline styles to gradient elements AND keep the class for CSS targeting
      clone.querySelectorAll('.gradient-slate').forEach((el) => {
        (el as HTMLElement).style.background = 'linear-gradient(135deg, #1e293b 0%, #334155 100%)';
        // Don't remove the class - CSS needs it for header layout rules
      });

      // Ensure white text on dark gradient backgrounds
      clone.querySelectorAll('[class*="gradient"], [style*="linear-gradient"]').forEach((el) => {
        const textElements = el.querySelectorAll('*');
        textElements.forEach(child => {
          const element = child as HTMLElement;
          if (element.textContent?.trim() && !element.style.color && !element.classList.contains('text-amber-400')) {
            element.style.color = '#ffffff';
          }
        });
      });

      // 4.5) Add CSS :target lightbox wrappers for images (screen-only)
      const overlayHtml: string[] = [];
      Array.from(clone.querySelectorAll('img')).forEach((img, idx) => {
        const id = `lb-${idx}`;
        const a = document.createElement('a');
        a.setAttribute('href', `#${id}`);
        a.setAttribute('class', 'lightbox-thumb');
        a.setAttribute('ondragstart', 'event.preventDefault();event.stopPropagation();');
        img.setAttribute('draggable', 'false');
        img.parentNode?.insertBefore(a, img);
        a.appendChild(img);
        overlayHtml.push(`<a href="#" id="${id}" class="image-modal-overlay lightbox"><img src="${img.src}" alt="${(img.alt || '').replace(/"/g, '&quot;')}" /></a>`);
      });

      // Build full HTML document
      const fullHTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    /* Force exact color rendering */
    * { 
      -webkit-print-color-adjust: exact !important; 
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* PDF Export - Proper margins, no full-bleed */
    @page { 
      size: Letter; 
      margin: 0.5in; 
    }

    #export-root {
      width: 7.5in;
      margin: 0 auto;
      padding: 0;
      box-sizing: border-box;
      overflow: hidden;
    }

    /* Force all pages to respect export-root width */
    #export-root .proposal-page {
      max-width: 100% !important;
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      box-sizing: border-box;
      overflow: hidden;
    }

    /* Constrain all child elements - no bleeding */
    #export-root * {
      max-width: 100%;
      box-sizing: border-box;
    }

    #export-root .gradient-slate,
    #export-root .proposal-header {
      margin: 0;
      box-sizing: border-box;
    }

    .proposal-page {
      break-after: page;
    }

    .proposal-page:last-child {
      break-after: auto;
    }

    .avoid-break,
    .interactive-3d {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /* Hide decorative absolute bars in print/export to prevent overlap */
    .interactive-3d .absolute { display: none !important; }

    .hero, .cover {
      height: auto !important;
      min-height: auto !important;
    }

    img {
      max-width: 100%;
      height: auto;
      display: block;
    }

    /* CSS :target Lightbox (screen only) */
    .lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.9); display: none; align-items: center; justify-content: center; z-index: 9999; padding: 2rem; }
    .lightbox img { max-width: 95vw; max-height: 95vh; object-fit: contain; }
    .lightbox:target { display: flex; }

    .cab-thumb img {
      object-fit: contain;
    }

    /* Export Header - Tidy layout */
    .export-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.75rem;
    }

    .export-header .logo img {
      display: block;
      max-height: 48px;
      width: auto;
      height: auto;
    }

    html, body { 
      margin: 0; 
      padding: 0; 
      background: #fff; 
      -webkit-print-color-adjust: exact; 
      print-color-adjust: exact; 
    }

    /* Gradient backgrounds */
    .gradient-slate {
      background: linear-gradient(135deg, #1e293b 0%, #334155 100%) !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* Text colors on dark backgrounds */
    .text-white { color: #ffffff !important; }
    .text-amber-400, .text-amber-500 { color: #fbbf24 !important; }
    .text-slate-300 { color: #cbd5e1 !important; }

    /* Hide builder UI and modals in PDF */
    .print\\:hidden, [data-lovable], .cursor-move, [class*="border-dashed"], input, button, 
    [role="dialog"], [data-radix-dialog], .image-modal-overlay, [class*="dialog"] {
      display: none !important;
    }
    
    /* Hide nav and buttons in print mode */
    @media print {
      .export-header .nav,
      .export-header .buttons {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <main id="export-root" class="bg-white">${clone.outerHTML}</main>
  ${overlayHtml.join('')}

  ${overlayHtml.join('')}
</body>
</html>`;

      const filename = `${proposal.proposal_name}_signed_${proposal.client_name}.pdf`;
      const requestBody: any = { filename, html: fullHTML };
      const { data, error } = await supabase.functions.invoke('generate-pdf', { body: requestBody });

      if (error) throw error;

      console.log('PDF generation response:', data);
      
      if (data?.url) {
        console.log('Downloading PDF from:', data.url);
        const link = document.createElement("a");
        link.href = data.url;
        link.download = data.filename || "proposal-signed.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Signed PDF downloaded successfully!');
      } else if (data?.pdf) {
        console.log('Using base64 PDF fallback');
        // Fallback: Convert base64 to blob and download
        const binary = atob(data.pdf);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'signed_proposal.pdf';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Signed PDF downloaded successfully!');
      } else {
        console.error('Invalid PDF response:', data);
        throw new Error(data?.error || "Failed to generate PDF - no URL or PDF data received");
      }
    } catch (error: any) {
      console.error('Error exporting signed PDF:', error);
      toast.error(error.message || 'Failed to generate signed PDF. Please try again or use browser print.');
    }
  };

  // Enhanced print preparation function
  const preparePrintVersion = () => {
    // Convert gradient classes to inline styles for better print compatibility
    const gradientElements = document.querySelectorAll('.gradient-slate, .proposal-header');
    gradientElements.forEach(el => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.background = 'linear-gradient(135deg, #1e293b 0%, #334155 100%)';
      htmlEl.style.setProperty('-webkit-print-color-adjust', 'exact');
      htmlEl.style.printColorAdjust = 'exact';
    });

    // Attach print-specific width constraints to the live DOM
    const container = document.querySelector('.proposal-container') as HTMLElement | null;
    if (container) {
      container.setAttribute('id', 'export-root-print');
      let style = document.getElementById('print-fixes') as HTMLStyleElement | null;
      if (!style) {
        style = document.createElement('style');
        style.id = 'print-fixes';
      }
      style.innerHTML = `@page{size:Letter;margin:0.5in;} *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;} #export-root-print{width:7.5in;margin:0 auto;padding:0;box-sizing:border-box;overflow:hidden;} #export-root-print .proposal-page{max-width:100% !important;width:100% !important;margin:0 !important;box-sizing:border-box;overflow:hidden;} .proposal-page{break-after:page;} .proposal-page:last-child{break-after:auto;} .avoid-break,.interactive-3d{break-inside:avoid;page-break-inside:avoid;} .interactive-3d .absolute{display:none !important;}`;
      document.head.appendChild(style);

      const cleanup = () => {
        container.removeAttribute('id');
        style && style.parentNode?.removeChild(style);
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);
    }

    // Ensure all images are loaded before triggering print
    const images = document.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    });

    Promise.all(imagePromises).then(() => {
      setTimeout(() => window.print(), 150);
    });
  };

  const exportClientHTML = async () => {
    try {
      // Ensure logo is loaded from company settings if not already set
      const proposalImages = { ...images };
      // Use creatorUserId in client mode, or session user in builder mode
      const userId = isClientMode ? creatorUserId : session?.user?.id;
      if (!proposalImages.logo && userId) {
        const { data: companyData } = await supabase
          .from('company_settings')
          .select('company_logo')
          .eq('user_id', userId)
          .maybeSingle();
        
        if (companyData?.company_logo) {
          proposalImages.logo = companyData.company_logo;
          setImages(proposalImages);
          // Wait for state to update and re-render
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      toast.info('Generating PDF...');

      // 1) Grab the live proposal DOM (builder preview) to keep formatting exact
      const container = document.querySelector('.proposal-container') as HTMLElement | null;
      if (!container) {
        toast.error('Nothing to export: proposal preview not found');
        return;
      }

      // Clone to avoid mutating the live preview
      const clone = container.cloneNode(true) as HTMLElement;

      // Remove print:hidden elements (like photo galleries) that should not appear in PDF
      clone.querySelectorAll('.print\\:hidden').forEach(el => el.remove());

      // 2) Upload base64 images to storage and replace with HTTPS URLs
      const uploadCache = new Map<string, string>(); // Cache by data URL to avoid re-uploads
      
      // Helper function to upload image and get public URL
      const uploadImageAndGetUrl = async (dataUrl: string): Promise<string | null> => {
        if (uploadCache.has(dataUrl)) {
          console.log('Using cached image URL');
          return uploadCache.get(dataUrl)!;
        }
        
        try {
          console.log(`Processing image for upload (size: ~${Math.round(dataUrl.length * 0.75 / 1024)}KB)...`);
          // Extract base64 data and mime type
          const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
          if (!matches) {
            console.error('Invalid data URL format');
            return null;
          }
          
          const [, mimeType, base64Data] = matches;
          const fileExt = mimeType.split('/')[1] || 'png';
          console.log(`Image type: ${mimeType}`);
          
          // Convert base64 to blob
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
          
          // Upload to Supabase storage
          const fileName = `pdf-export/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          console.log(`Uploading to storage: ${fileName}`);
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('proposal-media')
            .upload(fileName, blob, {
              contentType: mimeType,
              cacheControl: '3600',
            });
          
          if (uploadError) {
            console.error('Storage upload error:', uploadError);
            throw uploadError;
          }
          
          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('proposal-media')
            .getPublicUrl(fileName);
          
          console.log(`Successfully uploaded to: ${publicUrl}`);
          uploadCache.set(dataUrl, publicUrl);
          return publicUrl;
        } catch (e) {
          console.error('Failed to upload image:', e);
          return null;
        }
      };
      
      // Process all <img> tags - upload base64 data URLs to storage
      const imgElements = clone.querySelectorAll('img');
      console.log(`Found ${imgElements.length} images to process for regular PDF export`);
        const imagePromises = Array.from(imgElements).map(async (img, idx) => {
          const originalSrc = img.src || '';
          console.log(`Image ${idx} (${img.alt || 'no alt'}): src=${originalSrc.substring(0, 100)}...`);
          
          if (!originalSrc) {
            console.warn(`Image ${idx} has no src`);
            return;
          }
          
          // Ensure logo uses public HTTPS URL with eager loading
          if (originalSrc.startsWith('data:')) {
            console.log(`Processing data URL image ${idx}...`);
            const publicUrl = await uploadImageAndGetUrl(originalSrc);
            if (publicUrl) {
              console.log(`Successfully uploaded data URL image ${idx} to ${publicUrl}`);
              img.src = publicUrl;
              img.setAttribute('loading', 'eager');
              img.setAttribute('decoding', 'sync');
            } else {
              console.warn(`Failed to upload data URL image ${idx}, keeping data URL as fallback`);
            }
          } else if (originalSrc.startsWith(window.location.origin) || originalSrc.startsWith('/') || originalSrc.startsWith('./')) {
            // Convert local asset URL to public storage URL so external PDF service can access it
            try {
              console.log(`Externalizing local asset image ${idx}...`);
              const fullUrl = originalSrc.startsWith('/') || originalSrc.startsWith('./') 
                ? `${window.location.origin}${originalSrc.startsWith('./') ? originalSrc.substring(1) : originalSrc}` 
                : originalSrc;
              const res = await fetch(fullUrl);
              const blob = await res.blob();
              const mimeType = blob.type || 'image/png';
              const ext = (mimeType.split('/')[1] || 'png').split(';')[0];
              const fileName = `pdf-export/${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
              const { error: uploadError } = await supabase.storage
                .from('proposal-media')
                .upload(fileName, blob, { contentType: mimeType, cacheControl: '3600' });
              if (uploadError) throw uploadError;
              const { data: { publicUrl } } = supabase.storage
                .from('proposal-media')
                .getPublicUrl(fileName);
              console.log(`Local asset image ${idx} uploaded to ${publicUrl}`);
              img.src = publicUrl;
              img.setAttribute('loading', 'eager');
              img.setAttribute('decoding', 'sync');
            } catch (e) {
              console.error(`Failed to externalize local asset image ${idx}:`, e);
              // Keep original src as fallback
            }
          } else {
            console.log(`Image ${idx} already has public URL, keeping as-is`);
          }
        });
      
      await Promise.all(imagePromises);
      
      // Process background images using getComputedStyle to catch Tailwind bg-[url(...)]
      const bgElements = Array.from(clone.querySelectorAll('*')) as HTMLElement[];
      const bgPromises = bgElements.map(async (el) => {
        const computedBg = window.getComputedStyle(el).backgroundImage;
        if (!computedBg || computedBg === 'none') return;
        
        // Handle data URL images
        const match = computedBg.match(/url\(['"]?(data:[^'"]+)['"]?\)/);
        if (match) {
          const dataUrl = match[1];
          const publicUrl = await uploadImageAndGetUrl(dataUrl);
          if (publicUrl) {
            el.style.backgroundImage = `url(${publicUrl})`;
          }
        }
        // Preserve gradient backgrounds
        else if (computedBg.startsWith('linear-gradient')) {
          el.style.backgroundImage = computedBg;
        }
      });
      
      await Promise.all(bgPromises);

      // 3) Add inline styles to gradient elements AND keep the class for CSS targeting
      const gradientElements = clone.querySelectorAll('.gradient-slate');
      gradientElements.forEach(el => {
        (el as HTMLElement).style.background = 'linear-gradient(135deg, #1e293b 0%, #334155 100%)';
        // Don't remove the class - CSS needs it for header layout rules
      });

      // 4.5) Add CSS :target lightbox wrappers for images (screen-only)
      const overlayHtml: string[] = [];
      Array.from(clone.querySelectorAll('img')).forEach((img, idx) => {
        const id = `lb-${idx}`;
        // Wrap image in anchor for :target lightbox
        const a = document.createElement('a');
        a.setAttribute('href', `#${id}`);
        a.setAttribute('class', 'lightbox-thumb');
        a.setAttribute('ondragstart', 'event.preventDefault();event.stopPropagation();');
        img.setAttribute('draggable', 'false');
        img.parentNode?.insertBefore(a, img);
        a.appendChild(img);
        // Create overlay HTML (hidden by default, shown when targeted)
        overlayHtml.push(`<a href="#" id="${id}" class="image-modal-overlay lightbox"><img src="${img.src}" alt="${(img.alt || '').replace(/"/g, '&quot;')}" /></a>`);
      });
      const fullHTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${content.title || 'Proposal'} - ${content.clientName}</title>
  <link rel="canonical" href="${window.location.origin}" />
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    /* Force exact color rendering */
    * { 
      -webkit-print-color-adjust: exact !important; 
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* PDF Export - Proper margins, no full-bleed */
    @page { 
      size: Letter; 
      margin: 0.5in; 
    }

    #export-root {
      width: 7.5in;
      margin: 0 auto;
      padding: 0;
      box-sizing: border-box;
      overflow: hidden;
    }

    /* Force all pages to respect export-root width */
    #export-root .proposal-page {
      max-width: 100% !important;
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      box-sizing: border-box;
      overflow: hidden;
    }

    /* Constrain all child elements - no bleeding */
    #export-root * {
      max-width: 100%;
      box-sizing: border-box;
    }

    #export-root .gradient-slate,
    #export-root .proposal-header {
      margin: 0;
      box-sizing: border-box;
    }

    .proposal-page {
      break-after: page;
    }

    .proposal-page:last-child {
      break-after: auto;
    }

    .avoid-break,
    .interactive-3d {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /* Hide decorative absolute bars in print/export to prevent overlap */
    .interactive-3d .absolute { display: none !important; }

    .hero, .cover {
      height: auto !important;
      min-height: auto !important;
    }

    img {
      max-width: 100%;
      height: auto;
      display: block;
    }

    /* CSS :target Lightbox (screen only) */
    .lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.9); display: none; align-items: center; justify-content: center; z-index: 9999; padding: 2rem; }
    .lightbox img { max-width: 95vw; max-height: 95vh; object-fit: contain; }
    .lightbox:target { display: flex; }

    .cab-thumb img {
      object-fit: contain;
    }

    /* Export Header - Tidy layout */
    .export-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.75rem;
    }

    .export-header .logo img {
      display: block;
      max-height: 48px;
      width: auto;
      height: auto;
    }

    html, body { 
      margin: 0; 
      padding: 0; 
      background: #fff; 
      -webkit-print-color-adjust: exact; 
      print-color-adjust: exact; 
    }
    
    /* Gradient backgrounds - explicit for PDF rendering */
    .gradient-slate {
      background: linear-gradient(135deg, #1e293b 0%, #334155 100%) !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* Ensure white text on dark backgrounds */
    .text-white { color: #ffffff !important; }
    .text-amber-400, .text-amber-500 { color: #fbbf24 !important; }
    .text-slate-300 { color: #cbd5e1 !important; }

    /* Hide builder UI elements and modals in PDF */
    .print\\:hidden,
    [data-lovable],
    [class*="lovable"],
    [id*="lovable"],
    iframe,
    .cursor-move,
    [class*="border-dashed"],
    button,
    input[type="file"],
    [role="dialog"],
    [data-radix-dialog],
    .image-modal-overlay,
    [class*="dialog"] {
      display: none !important; 
      visibility: hidden !important; 
      opacity: 0 !important;
    }

    /* Hide nav and buttons in print mode */
    @media print {
      .export-header .nav,
      .export-header .buttons {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <main id="export-root" class="bg-white">${clone.outerHTML}</main>
</body>
</html>`;

      const filename = `${content.projectName || 'proposal'}.pdf`;
      const requestBody: any = { filename, html: fullHTML };
      const { data, error } = await supabase.functions.invoke('generate-pdf', { body: requestBody });

      if (error) {
        console.error('PDF generation error:', error);
        toast.error('PDF generation failed. Please try again.');
        return;
      }

      console.log('PDF generation response:', data);

      if (data?.url) {
        console.log('Downloading PDF from:', data.url);
        // Download from storage URL
        const link = document.createElement("a");
        link.href = data.url;
        link.download = data.filename || `${content.projectName || 'proposal'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('PDF exported successfully!');
      } else if (data?.pdf) {
        console.log('Using base64 PDF fallback');
        // Fallback: Convert base64 to blob and download
        const byteCharacters = atob(data.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `${content.projectName || 'proposal'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success('PDF exported successfully!');
      } else {
        console.error('PDF generation returned no data:', data);
        toast.error(data?.error || 'No PDF returned. Please try again or use browser print.');
        return;
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF. Please try again.');
    }
  };


  // Client view: clean proposal only (no auth required)
  // Load proposal from backend function; fallback to preview if unavailable
  useEffect(() => {
    if (!isClientMode) return;
    if (typeof window === 'undefined') return; // SSR-safe
    (async () => {
      try {
        if (creatorUserId) {
          const { data, error } = await supabase.functions.invoke('get-proposal', {
            body: { creatorUserId, proposalName: proposalNameParam }
          });
          if (error) throw error;
          if (data?.content && data?.images) {
            setContent(data.content);
            setImages(data.images);
            setAdditionalPhotos((data.additional_photos || []).map((p: any) => ({
              ...p,
              isInspiration: p.isInspiration ?? false,
              showInClient: p.showInClient ?? true,
            })));
            setCurrentProposalName(data.proposal_name || '');
            
            // Also load company logo from settings if not in proposal
            if (!data.images.logo) {
              const { data: companyData } = await supabase
                .from('company_settings')
                .select('company_logo')
                .eq('user_id', creatorUserId)
                .maybeSingle();
              
              if (companyData?.company_logo) {
                setImages(prev => ({ ...prev, logo: companyData.company_logo }));
              }
            }
            return; // Loaded successfully
          }
        }
      } catch (e) {
        console.error('Failed to load proposal from backend:', e);
      }
      // Fallback to localStorage preview
      try {
        const previewData = localStorage.getItem('proposal_preview');
        if (previewData) {
          const parsed = JSON.parse(previewData);
          setContent(parsed.content);
          setImages(parsed.images);
          setAdditionalPhotos(parsed.additionalPhotos || []);
          setCurrentProposalName(parsed.proposalName || '');
        }
      } catch (e) {
        console.error('Failed to load preview data:', e);
      }
    })();
  }, [isClientMode, creatorUserId, proposalNameParam]);

  // Show loading while checking auth
  if (loading && !isClientMode) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (isClientMode) {
    return (
      <div className="min-h-screen bg-white">
      <div className="bg-white shadow-sm border-b border-slate-200 print:hidden sticky top-0 z-50">
          <div className="max-w-4xl mx-auto p-4 text-center">
            <h1 className="text-2xl font-light text-slate-800 mb-2">{(content.title.split('—')[0] || content.title).trim()}</h1>
            <div className="text-sm text-slate-600 space-x-4 mb-4">
              <span className="font-medium">Project: {content.projectName}</span>
              <span>•</span>
              <span>Client: {content.clientName}</span>
              <span>•</span>
              <span>Project #: {content.projectNumber}</span>
            </div>
            <button 
              onClick={exportClientHTML}
              className="bg-slate-800 text-white px-8 py-3 hover:bg-slate-900 transition-colors font-medium tracking-wide uppercase text-sm shadow-lg"
            >
              Download PDF
            </button>
          </div>
        </div>
        <div className="proposal-container max-w-5xl mx-auto bg-white">
          <ProposalPage1 content={content} images={images} onLogoDrop={undefined} />
          <ProposalPage2 content={content} images={images} additionalPhotos={additionalPhotos.filter(p => p.showInClient)} onOptionImageDrop={undefined} onFloorPlanImageDrop={undefined} isClientMode={true} />
          <ProposalPage3 content={content} images={images} proposalName={currentProposalName} isClientMode={true} creatorUserId={creatorUserId} />
        </div>
      </div>
    );
  }

  // Builder view: with controls and live preview
  return (
    <div className="min-h-screen bg-slate-100">
      <BuilderPanel
        content={content}
        images={images}
        additionalPhotos={additionalPhotos}
        currentProposalName={currentProposalName}
        session={session}
        savedProposals={savedProposals}
        onContentChange={handleContentChange}
        onImageUpload={handleImageUpload}
        onOptionImageUpload={handleOptionImageUpload}
        onAdditionalPhotoUpload={handleAdditionalPhotoUpload}
        onInspirationPhotoUpload={handleInspirationPhotoUpload}
        onRemoveAdditionalPhoto={removeAdditionalPhoto}
        onUpdatePhotoCaption={updatePhotoCaption}
        onTogglePhotoVisibility={togglePhotoVisibility}
        onTimelineChange={handleTimelineChange}
        onNextStepChange={handleNextStepChange}
        onAddTimelineItem={addTimelineItem}
        onRemoveTimelineItem={removeTimelineItem}
        onAddNextStep={addNextStep}
        onRemoveNextStep={removeNextStep}
        onSave={handleSaveClick}
        onLoad={() => setShowLoadDialog(true)}
        onExport={exportClientHTML}
        onClientView={async () => {
          try {
            const creatorId = session?.user?.id || '';

            if (creatorId) {
              // Ensure latest changes are saved so client view loads fresh data
              const targetName = (currentProposalName && currentProposalName.trim().length > 0)
                ? currentProposalName
                : (content.projectName?.trim() || 'Proposal');

              await saveProposal(targetName, false);

              const url = `${window.location.origin}/?client=true&creator=${creatorId}&proposal=${encodeURIComponent(targetName)}`;
              const win = window.open(url, '_blank');
              if (!win || win.closed || typeof win.closed === 'undefined') {
                window.location.href = url;
              }
              return;
            }

            // Fallback: lightweight preview (avoid quota errors by omitting large images)
            try {
              localStorage.setItem('proposal_preview', JSON.stringify({
                content,
                images: { logo: null, floorPlan: null, optionImages: {}, vanityImages: {} },
                additionalPhotos: [],
                proposalName: currentProposalName
              }));
            } catch (_) {
              // Ignore storage quota errors silently
            }
            const url = `${window.location.origin}/?client=true`;
            const win = window.open(url, '_blank');
            if (!win || win.closed || typeof win.closed === 'undefined') {
              window.location.href = url;
            }
          } catch (error) {
            console.error('Failed to open client view:', error);
            // Last resort: navigate without params
            window.location.href = `${window.location.origin}/?client=true`;
          }
        }}
        onPrint={preparePrintVersion}
        onViewSignedProposal={handleViewSignedProposal}
        onExportSignedPDF={handleExportSignedPDF}
      />

      <SavedProposalsDialog
        open={showLoadDialog}
        onOpenChange={setShowLoadDialog}
        proposals={savedProposals}
        onLoad={loadProposal}
        onDelete={deleteProposal}
      />

      <SaveProposalDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        onSave={saveProposal}
        currentProposalName={currentProposalName}
      />

      {/* Live preview area */}
      <div className="proposal-container max-w-5xl mx-auto bg-white">
        <ProposalPage1 content={content} images={images} onLogoDrop={handleLogoDrop} />
        <ProposalPage2 content={content} images={images} additionalPhotos={additionalPhotos} onOptionImageDrop={handleOptionImageDrop} onFloorPlanImageDrop={handleFloorPlanDrop} isClientMode={false} />
        <ProposalPage3 content={content} images={images} proposalName={currentProposalName} isClientMode={false} creatorUserId={session?.user?.id} />
      </div>
    </div>
  );
};

export default Index;
